import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cva, type VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import { Button as GrommetButton, ButtonExtendedProps, Text } from 'grommet';
import { ComponentProps, HTMLAttributes, ReactNode } from 'preact/compat';

import { cn } from '@/lib/utils';

import style from './style.scss';

export interface ZLButtonProps extends Omit<ButtonExtendedProps, 'loading'> {
  loading?: boolean;
}

const getLabel = (loading: boolean | string | undefined, label: ReactNode): string | ReactNode => {
  if (loading && typeof loading === 'boolean') {
    return <FontAwesomeIcon icon={faSpinner} spin style={{ verticalAlign: 'middle', fontSize: '16px' }} />;
  }
  if (loading && typeof loading === 'string') {
    return loading;
  }

  return label;
};

export const PrimaryButton = ({ loading, label, size = 'large', className, ...args }: ZLButtonProps) => (
  <GrommetButton
    primary
    size={size}
    className={clsx(className, style[size], { [style.loading]: loading })}
    label={getLabel(loading, label)}
    /* eslint-disable-next-line react/jsx-props-no-spreading */
    {...args}
  />
);

export const OutlineButton = ({ loading, label, size = 'large', className, ...args }: ZLButtonProps) => (
  <GrommetButton
    secondary
    size={size}
    className={clsx(className, style[size], style.outline, { [style.loading]: loading })}
    label={getLabel(loading, label)}
    /* eslint-disable-next-line react/jsx-props-no-spreading */
    {...args}
  />
);
export const NewOutlineButton = ({ loading, label, size = 'large', className, ...args }: ZLButtonProps) => (
  <GrommetButton
    secondary
    size={size}
    className={clsx(className, style[size], style.newOutlineButton, { [style.loading]: loading })}
    label={getLabel(loading, label)}
    /* eslint-disable-next-line react/jsx-props-no-spreading */
    {...args}
  />
);

export const OutlineButtonDanger = ({ loading, label, size = 'large', className, ...args }: ZLButtonProps) => (
  <GrommetButton
    secondary
    size={size}
    className={clsx(style.danger, className, style[size], style.outline, { [style.loading]: loading })}
    label={<Text color="var(--red)">{getLabel(loading, label as string) as string}</Text>}
    /* eslint-disable-next-line react/jsx-props-no-spreading */
    {...args}
  />
);

export const PrimaryYellow = ({ loading, label, size = 'large', type, className, ...args }: ZLButtonProps) => (
  <GrommetButton
    type={type}
    size={size}
    className={clsx(style.yellow, className, style[size], { [style.loading]: loading })}
    label={getLabel(loading, label ?? '')}
    /* eslint-disable-next-line react/jsx-props-no-spreading */
    {...args}
  />
);

export const PrimarySecondaryGreen = ({ loading, label, size = 'large', type, className, ...args }: ZLButtonProps) => (
  <GrommetButton
    type={type}
    size={size}
    className={clsx(style.secondaryGreen, className, style[size], { [style.loading]: loading })}
    label={getLabel(loading, label)}
    /* eslint-disable-next-line react/jsx-props-no-spreading */
    {...args}
  />
);

export const LandingPrimary = ({ className, children, type = 'button', ...rest }: HTMLAttributes<HTMLButtonElement>) => (
  // eslint-disable-next-line react/button-has-type, react/jsx-props-no-spreading
  <button type={type} className={clsx(style.landingPrimary, className)} {...rest}>
    {children}
  </button>
);

export const LandingSecondary = ({ className, children, type = 'button', ...rest }: HTMLAttributes<HTMLButtonElement>) => (
  // eslint-disable-next-line react/button-has-type, react/jsx-props-no-spreading
  <button type={type} className={clsx(style.landingSecondary, className)} {...rest}>
    {children}
  </button>
);

export const buttonVariants = cva(
  "focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-system-red aria-invalid:ring-system-red/20 dark:aria-invalid:ring-system-red/40 inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium outline-none transition-all focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        plain: 'rounded-none',
        default: 'shadow-xs bg-primary-green text-white hover:bg-secondary-green',
        yellow: 'shadow-xs bg-primary-yellow text-black hover:bg-secondary-yellow',
        tea: 'shadow-xs bg-tea-100 text-forest-dark hover:bg-tea-200',
        destructive:
          'shadow-xs hover:bg-system-red/90 focus-visible:ring-system-red/20 dark:bg-system-red/60 dark:focus-visible:ring-system-red/40 bg-system-red text-white',
        outline:
          'shadow-xs border border-primary-green bg-white text-primary-green hover:bg-secondary-green hover:text-white',
        // secondary:
        //   'border-secondary-foreground bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80 border',
        // ghost: 'text-muted-foreground hover:text-foreground hover:bg-transparent',
        link: 'underline-offset-4 hover:underline',
        // classroom:
        //   'rounded-full border-2 border-neutral-200/50 bg-neutral-400/50 text-white backdrop-blur-[2px] hover:border-neutral-200 hover:bg-neutral-400',
        // mediaFilter: 'rounded-full bg-[#F1F1F1] text-stone-500 hover:opacity-50',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        xs: 'h-6 px-3.5 text-xs has-[>svg]:px-2.5',
        sm: 'h-8 gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 px-6 has-[>svg]:px-4',
        icon: 'size-9',
        manual: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export const Button = ({
  className,
  variant,
  size,
  ...args
}: Omit<ComponentProps<'button'>, 'size'> & VariantProps<typeof buttonVariants>) => (
  <button
    type="button"
    className={cn('preflight preflight-button', buttonVariants({ variant, size, className }))}
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...args}
  />
);

export const ButtonLink = ({
  className,
  variant,
  size,
  ...args
}: Omit<ComponentProps<'a'>, 'size'> & VariantProps<typeof buttonVariants>) => (
  // eslint-disable-next-line jsx-a11y/anchor-has-content
  <a
    className={cn('preflight preflight-a', buttonVariants({ variant, size, className }))}
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...args}
  />
);
